/**
 * /sources page — Sources Spine read-cutover + 3-bucket redesign (Phase 1 PR 11).
 *
 * The triage view for sender identity. Replaces the legacy IP-grouped
 * page; reads from the new `sources` table populated by the matcher.
 *
 * Page structure (sources-spine-implementation-plan.md § PR 11):
 *
 *   Needs Work (top, expanded by default):
 *     (Unclassified AND not pinned AND not dismissed)
 *     OR (Approved AND Misconfigured)
 *     — sorted desc by messagesFailingInWindow (the at-risk metric;
 *     same number in both Rollout and Operate mode, just framed
 *     differently in the UI).
 *
 *   Review (collapsed, amber):
 *     confidence_pinned = true sources + open matcher_review_required
 *     action items. The "computer needs you to look at this" bucket.
 *
 *   Ready (collapsed, green):
 *     Approved + Aligned sources, plus a Forwarders subgroup
 *     (Forwarder + Aligned). Approved sources with Insufficient-Data
 *     also live here — the user has settled it; we just haven't
 *     observed enough yet.
 *
 *   Hidden by default (re-surface via "Show dismissed" toggle):
 *     dismissed_at IS NOT NULL OR classification = 'Not-Mine'.
 *
 * Data flow: a single GET /api/sources call returns every source +
 * bucket assignment + window aggregate. Filtering/grouping happens
 * client-side so changing the search input doesn't refetch.
 *
 * Time window: subscribed to the global TimeWindowSelector (PR 9). The
 * trailing-window aggregate (`messagesInWindow`,
 * `messagesFailingInWindow`) re-fetches when the window changes.
 *
 * Bulk: select multiple rows → SourceClassifyControl in bulk mode
 * applies the same classification across the selection. Uses PR 10's
 * POST /api/sources/bulk-classify; cross-org ids drop silently
 * server-side.
 */

import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Search,
  ShieldCheck,
  ShieldAlert,
  HelpCircle,
  Eye,
  EyeOff,
  ChevronDown,
  ChevronRight,
  Loader2,
  AlertTriangle,
  CheckCircle2,
  Globe,
  ArrowRight,
  Sparkles,
  Lock,
  Repeat,
  Pin,
  User as UserIcon,
} from "lucide-react";
import {
  api,
  type SpineClassification,
  type SpineHealth,
  type SpineSourceListResponse,
  type SpineSourceListRow,
  type SpineMatcherReviewItem,
  type SpineDomainSummary,
  type SpineOwnerCandidate,
} from "@/lib/api";
import { SourceClassifyControl } from "@/components/sources/SourceClassifyControl";
import { SpfBudgetClassifyGuard } from "@/components/sources/SpfBudgetClassifyGuard";
import { RecommendationText } from "@/components/sources/RecommendationText";
import { SourceOwnerSelect } from "@/components/sources/SourceOwnerSelect";
import { timeWindowToDays, useTimeWindowStore } from "@/lib/time-window";
import { getOrgDomain, wellKnownAsnLabel } from "@dmarc-platform/shared";
import { toast } from "@/lib/toast";

// ---------------------------------------------------------------------------
// SpineSourcesView — the canonical sources triage component.
// ---------------------------------------------------------------------------

/**
 * Props for the reusable spine sources view. The component is mounted
 * two places (PR 16.1):
 *
 *   - `<Sources />` at `/sources` — no props; the org-wide cross-domain
 *     view with a domain selector dropdown for filtering.
 *
 *   - Inside the per-domain detail page's Sources tab — with
 *     `lockedDomainId` set to the route's domain id. The domain
 *     selector is hidden and the URL `?domain=` param is ignored;
 *     the view is locked to the parent domain.
 *
 * Same data, same buckets, same controls everywhere — the only
 * difference is whether the user can switch domain in-place. This
 * eliminates the pre-PR-16.1 split where the /domains/:id Sources tab
 * was a stale legacy implementation reading from `sending_sources`.
 */
interface SpineSourcesViewProps {
  /**
   * When set, scopes the view to this domain and hides the domain
   * selector + the page-level header (the parent surface provides
   * its own header). When unset, the view renders its own header and
   * the selector drives the URL `?domain=` param.
   */
  lockedDomainId?: string;
}

export function SpineSourcesView({
  lockedDomainId,
}: SpineSourcesViewProps = {}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const window = useTimeWindowStore((s) => s.window);
  const windowDays = timeWindowToDays(window);

  // When mounted with a locked domain (per-domain tab), ignore the
  // URL `?domain=` param and use the lock. Otherwise use the URL.
  const domainIdFilter = lockedDomainId ?? searchParams.get("domain");

  // Owner filter (PR 20). URL-persisted so a shared link reproduces the
  // filtered view. Three modes: omitted (any), "unassigned", or a user id.
  const ownerFilter = searchParams.get("owner");

  const [data, setData] = useState<SpineSourceListResponse | null>(null);
  // Matcher-review items now come from /api/action-items (PR 14 cutover).
  // Before PR 14 the Review bucket read data.matcherReviewItems from
  // /api/sources; that field is still returned for back-compat but is
  // no longer consumed here.
  const [matcherReviewItems, setMatcherReviewItems] = useState<
    SpineMatcherReviewItem[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchText, setSearchText] = useState("");
  const [showHidden, setShowHidden] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({
    review: true,
    ready: true,
    // PR 60 — Threats default collapsed too. The section is supposed
    // to be glanceable ("297 spoofs this week, click to see who"),
    // not part of the primary triage flow.
    threats: true,
  });

  // Fetch on mount + whenever the window or domain filter changes.
  // Two parallel calls: spine sources + unified action items (we only
  // consume the matcher_review_required leg, but the endpoint returns
  // a merged list — filter client-side).
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([
      api.getAllSources({
        windowDays,
        domainId: domainIdFilter ?? undefined,
        ownerUserId: ownerFilter ?? undefined,
      }),
      // kind=alert restricts to event rows (skip the standing-condition
      // leg — the Review bucket only cares about matcher_review events
      // here; pinned sources come from the spine response directly).
      api.getActionItems({
        domainId: domainIdFilter ?? undefined,
        kind: "alert",
        status: "active",
        limit: 200,
      }),
    ])
      .then(([sourcesResp, actionItemsResp]) => {
        if (cancelled) return;
        setData(sourcesResp);
        setMatcherReviewItems(
          actionItemsResp.items
            .filter((item) => item.category === "matcher_review_required")
            .map(actionItemToMatcherReview)
        );
        // Drop selections for ids no longer in the result (window change
        // typically keeps everything, but a domain switch will filter).
        setSelectedIds((prev) => {
          const valid = new Set(sourcesResp.sources.map((s) => s.id));
          const next = new Set<string>();
          for (const id of prev) if (valid.has(id)) next.add(id);
          return next;
        });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [windowDays, domainIdFilter, ownerFilter]);

  // Re-fetch helper passed down to row controls so a classify/dismiss
  // refreshes the bucket assignment server-side. Both legs re-fire
  // because a classify can also resolve a standing condition that
  // would otherwise show up in the unified action-items feed.
  function refresh() {
    Promise.all([
      api.getAllSources({
        windowDays,
        domainId: domainIdFilter ?? undefined,
        ownerUserId: ownerFilter ?? undefined,
      }),
      api.getActionItems({
        domainId: domainIdFilter ?? undefined,
        kind: "alert",
        status: "active",
        limit: 200,
      }),
    ])
      .then(([sourcesResp, actionItemsResp]) => {
        setData(sourcesResp);
        setMatcherReviewItems(
          actionItemsResp.items
            .filter((item) => item.category === "matcher_review_required")
            .map(actionItemToMatcherReview)
        );
      })
      .catch((err: unknown) => {
        toast.error(
          "Couldn't refresh sources",
          err instanceof Error ? err.message : undefined
        );
      });
  }

  function handleDomainChange(next: string | null) {
    const params = new URLSearchParams(searchParams);
    if (next) params.set("domain", next);
    else params.delete("domain");
    setSearchParams(params, { replace: true });
  }

  function handleOwnerChange(next: string | null) {
    const params = new URLSearchParams(searchParams);
    if (next) params.set("owner", next);
    else params.delete("owner");
    setSearchParams(params, { replace: true });
  }

  function toggleCollapsed(key: string) {
    setCollapsed((c) => ({ ...c, [key]: !c[key] }));
  }

  function toggleSelect(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function selectAll(rows: SpineSourceListRow[]) {
    setSelectedIds(new Set(rows.map((r) => r.id)));
  }

  function clearSelection() {
    setSelectedIds(new Set());
  }

  // --- Derived data --------------------------------------------------------

  const buckets = useMemo(() => groupSources(data?.sources ?? [], showHidden), [
    data?.sources,
    showHidden,
  ]);
  const filteredBuckets = useMemo(
    () => filterBuckets(buckets, searchText),
    [buckets, searchText]
  );
  // PR 64 (#2) — likely-duplicate hint. The matcher's synthetic key
  // is (domain, asn, /24); an observation that arrives before IP
  // enrichment runs gets `asn = "AS-UNKNOWN"`, so the same /24 ends
  // up split into one "real ASN" source and one "AS-UNKNOWN" source.
  // The reviewer saw two Mimecast rows (9,345 and 9,264 msgs) on
  // 170.10.153.0/24 — one with AS30031, one with AS-UNKNOWN. We
  // detect this pattern client-side and surface an inline "likely
  // duplicate of X" hint on the AS-UNKNOWN row, linking to the
  // canonical row. Generic across any (domain, cidr) — no provider/
  // ASN-specific matching.
  const duplicateOf = useMemo(
    () => detectAsnUnknownDuplicates(data?.sources ?? []),
    [data?.sources]
  );

  const selectedDomain: SpineDomainSummary | null = useMemo(() => {
    if (!domainIdFilter || !data) return null;
    return data.domains.find((d) => d.id === domainIdFilter) ?? null;
  }, [data, domainIdFilter]);

  // Render -----------------------------------------------------------------

  return (
    <div className="space-y-6">
      {/* Page header — only on the standalone /sources surface. When
          mounted as a tab inside the domain detail page the parent
          provides its own h1 + tab strip, so suppress this. */}
      {!lockedDomainId && (
        <header className="space-y-2">
          <div className="flex items-baseline justify-between">
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Sources</h1>
              <p className="text-sm text-slate-600">
                Every sender identity DMARC has observed for your domains.
                Triage what needs your attention; the rest stays out of your
                way.
              </p>
            </div>
            <span className="text-xs text-slate-500">
              Trailing {windowDays} day{windowDays === 1 ? "" : "s"}
            </span>
          </div>
        </header>
      )}

      {/* Filter row */}
      <div className="flex flex-wrap items-center gap-3 bg-white border border-slate-200 rounded-lg p-3">
        {/* Domain selector — hidden when the domain is locked by the
            parent surface (domain detail Sources tab). */}
        {!lockedDomainId && (
          <DomainSelector
            domains={data?.domains ?? []}
            selectedId={domainIdFilter}
            onChange={handleDomainChange}
          />
        )}

        <div className="flex-1 min-w-[200px] relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Search provider, identity, IP…"
            className="w-full pl-9 pr-3 py-1.5 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sky-300"
          />
        </div>

        <OwnerFilter selected={ownerFilter} onChange={handleOwnerChange} />

        <button
          type="button"
          onClick={() => setShowHidden((s) => !s)}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-sm transition-colors ${
            showHidden
              ? "bg-slate-200 border-slate-400 text-slate-800"
              : "bg-white border-slate-300 text-slate-600 hover:bg-slate-50"
          }`}
          aria-pressed={showHidden}
          title="Includes dismissed sources and Not-Mine sources"
        >
          {showHidden ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
          {showHidden ? "Showing hidden" : "Show hidden"}
        </button>
      </div>

      {/* Tier upsell banner */}
      {data && !data.permissions.canViewHostname && (
        <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-lg p-4">
          <Lock className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1 text-sm">
            <div className="font-medium text-amber-900">
              Provider names hidden on the free tier
            </div>
            <div className="text-amber-800">
              You can still see counts, classification, and health. Upgrade to
              Starter to see who's actually sending mail as you.
            </div>
          </div>
        </div>
      )}

      {/* Error state */}
      {error && (
        <div className="flex items-center gap-3 bg-rose-50 border border-rose-200 rounded-lg p-4 text-sm text-rose-900">
          <AlertTriangle className="w-5 h-5" />
          <div>
            <div className="font-medium">Couldn't load sources</div>
            <div className="text-rose-800">{error}</div>
          </div>
        </div>
      )}

      {/* Loading state */}
      {loading && !data && (
        <div className="flex items-center justify-center py-16 text-slate-500">
          <Loader2 className="w-5 h-5 animate-spin mr-2" />
          Loading sources…
        </div>
      )}

      {/* Empty state */}
      {!loading && data && data.sources.length === 0 && (
        <div className="text-center py-16 bg-white border border-slate-200 rounded-lg">
          <Sparkles className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <div className="text-base font-medium text-slate-700">
            No sources yet
          </div>
          <div className="text-sm text-slate-500 max-w-md mx-auto mt-1">
            Once DMARC reports arrive for{" "}
            {selectedDomain ? (
              <span className="font-medium">{selectedDomain.name}</span>
            ) : (
              "your verified domains"
            )}
            , sender identities will show up here for you to triage.
          </div>
        </div>
      )}

      {/* Buckets */}
      {data && data.sources.length > 0 && (
        <div className="space-y-4">
          <BucketSection
            kind="needs_work"
            title="Needs Work"
            description="What's blocking enforcement progress, or actively being rejected."
            sources={filteredBuckets.needsWork}
            mode={selectedDomain?.mode ?? null}
            collapsed={false}
            permissions={data.permissions}
            selectedIds={selectedIds}
            onToggleSelect={toggleSelect}
            onSelectAll={() => selectAll(filteredBuckets.needsWork)}
            onClassified={refresh}
            onDismissed={refresh}
            onOwnerChanged={refresh}
            duplicateOf={duplicateOf}
          />

          {/* PR 60 — Threats. Hostile traffic the matcher auto-
              classified as Not-Mine (presumed-spoof rule) plus any
              user-flagged Not-Mine. Visible by default but
              collapsed-with-preview; not part of the triage flow. */}
          {filteredBuckets.threats.length > 0 && (
            <BucketSection
              kind="threats"
              title="Threats"
              description="Auto-classified spoof attempts and user-flagged Not-Mine sources. Already neutralised — no action required, but visible so you can see what's being attempted against your domain."
              sources={filteredBuckets.threats}
              mode={null}
              collapsed={collapsed.threats!}
              onToggleCollapsed={() => toggleCollapsed("threats")}
              permissions={data.permissions}
              selectedIds={selectedIds}
              onToggleSelect={toggleSelect}
              onSelectAll={() => selectAll(filteredBuckets.threats)}
              onClassified={refresh}
              onDismissed={refresh}
              onOwnerChanged={refresh}
            />
          )}

          <BucketSection
            kind="review"
            title="Review"
            description="The matcher needs a human to look at these — split, merge, or classify."
            sources={filteredBuckets.review}
            matcherReviewItems={
              searchText
                ? filterMatcherItems(matcherReviewItems, searchText)
                : matcherReviewItems
            }
            mode={null}
            collapsed={collapsed.review!}
            onToggleCollapsed={() => toggleCollapsed("review")}
            permissions={data.permissions}
            selectedIds={selectedIds}
            onToggleSelect={toggleSelect}
            onSelectAll={() => selectAll(filteredBuckets.review)}
            onClassified={refresh}
            onDismissed={refresh}
            onOwnerChanged={refresh}
            duplicateOf={duplicateOf}
          />

          <BucketSection
            kind="ready"
            title="Ready"
            description="Settled and aligned. Nothing to do unless something changes."
            sources={filteredBuckets.ready.approved}
            forwarderSources={filteredBuckets.ready.forwarders}
            mode={null}
            collapsed={collapsed.ready!}
            onToggleCollapsed={() => toggleCollapsed("ready")}
            permissions={data.permissions}
            selectedIds={selectedIds}
            onToggleSelect={toggleSelect}
            onSelectAll={() =>
              selectAll([
                ...filteredBuckets.ready.approved,
                ...filteredBuckets.ready.forwarders,
              ])
            }
            onClassified={refresh}
            onDismissed={refresh}
            onOwnerChanged={refresh}
          />

          {showHidden && filteredBuckets.hidden.length > 0 && (
            <BucketSection
              kind="hidden"
              title="Hidden"
              description="Dismissed or marked as Not-Mine."
              sources={filteredBuckets.hidden}
              mode={null}
              collapsed={false}
              permissions={data.permissions}
              selectedIds={selectedIds}
              onToggleSelect={toggleSelect}
              onSelectAll={() => selectAll(filteredBuckets.hidden)}
              onClassified={refresh}
              onDismissed={refresh}
              onOwnerChanged={refresh}
            />
          )}
        </div>
      )}

      {/* Bulk action bar */}
      {selectedIds.size > 0 && (
        <BulkActionBar
          count={selectedIds.size}
          sourceIds={Array.from(selectedIds)}
          onClear={clearSelection}
          onClassified={() => {
            clearSelection();
            refresh();
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Domain selector
// ---------------------------------------------------------------------------

interface DomainSelectorProps {
  domains: SpineDomainSummary[];
  selectedId: string | null;
  onChange: (next: string | null) => void;
}

function DomainSelector({ domains, selectedId, onChange }: DomainSelectorProps) {
  if (domains.length <= 1) {
    return null;
  }
  return (
    <div className="flex items-center gap-2">
      <Globe className="w-4 h-4 text-slate-500" />
      <select
        value={selectedId ?? ""}
        onChange={(e) => onChange(e.target.value || null)}
        className="text-sm border border-slate-300 rounded-md px-2 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-sky-300"
      >
        <option value="">All domains ({domains.length})</option>
        {domains.map((d) => (
          <option key={d.id} value={d.id}>
            {d.name}
          </option>
        ))}
      </select>
    </div>
  );
}

/**
 * OwnerFilter (Phase 2 PR 20) — page-level filter for "show me sources
 * I own / nobody owns / all sources". Lazily loads the candidate list
 * on first open so unmount/remount on tab switch doesn't pay the fetch
 * cost twice. Selection is URL-persisted by the parent's
 * handleOwnerChange so a shareable link reproduces the view.
 *
 * Layout note: the trigger is a native <select> for consistency with
 * the domain selector, but the candidates load asynchronously so the
 * empty state renders "Loading…" until the first request lands. That
 * matches the keyboard / a11y expectations of a real dropdown without
 * requiring a custom popover.
 */
function OwnerFilter({
  selected,
  onChange,
}: {
  selected: string | null;
  onChange: (next: string | null) => void;
}) {
  const [users, setUsers] = useState<SpineOwnerCandidate[] | null>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .getSourceOwners()
      .then((r) => {
        if (!cancelled) setUsers(r.users);
      })
      .catch(() => {
        // Soft-fail; the filter just stays at "All owners". Errors on
        // the org-members fetch shouldn't block the page.
      })
      .finally(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Don't render the filter UI for solo-user orgs — the only meaningful
  // selection would always be "yourself", and an always-on dropdown
  // creates UI noise without UX value. Once the org grows past one
  // user the filter appears automatically on next page load.
  if (loaded && (users?.length ?? 0) <= 1) {
    return null;
  }

  return (
    <div className="flex items-center gap-2">
      <UserIcon className="w-4 h-4 text-slate-500" />
      <select
        value={selected ?? ""}
        onChange={(e) => onChange(e.target.value || null)}
        className="text-sm border border-slate-300 rounded-md px-2 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-sky-300"
        aria-label="Filter sources by owner"
      >
        <option value="">All owners</option>
        <option value="unassigned">Unassigned</option>
        {(users ?? []).map((u) => (
          <option key={u.id} value={u.id}>
            {u.name && u.name.trim().length > 0
              ? u.name
              : u.email.split("@")[0]}
          </option>
        ))}
      </select>
    </div>
  );
}

// ---------------------------------------------------------------------------
// BucketSection
// ---------------------------------------------------------------------------

type BucketKind = "needs_work" | "review" | "ready" | "threats" | "hidden";

interface BucketSectionProps {
  kind: BucketKind;
  title: string;
  description: string;
  sources: SpineSourceListRow[];
  forwarderSources?: SpineSourceListRow[];
  matcherReviewItems?: SpineMatcherReviewItem[];
  /**
   * Domain mode used for the at-risk label framing in Needs Work. null
   * means multi-domain or non-Needs-Work bucket; falls back to generic
   * wording.
   */
  mode: string | null;
  collapsed: boolean;
  onToggleCollapsed?: () => void;
  permissions: SpineSourceListResponse["permissions"];
  selectedIds: Set<string>;
  onToggleSelect: (id: string) => void;
  onSelectAll: () => void;
  onClassified: () => void;
  onDismissed: () => void;
  onOwnerChanged: () => void;
  /** PR 64 (#2) — `Map<asUnknownSourceId, canonicalSource>` plumbed
   *  through to inline-render the duplicate hint per row. Optional. */
  duplicateOf?: Map<string, SpineSourceListRow>;
}

function BucketSection(props: BucketSectionProps) {
  const {
    kind,
    title,
    description,
    sources,
    forwarderSources = [],
    matcherReviewItems = [],
    mode,
    collapsed,
    onToggleCollapsed,
    permissions,
    selectedIds,
    onToggleSelect,
    onSelectAll,
    onClassified,
    onDismissed,
    onOwnerChanged,
    duplicateOf,
  } = props;

  const total = sources.length + forwarderSources.length + matcherReviewItems.length;
  const colors = bucketColors(kind);

  return (
    <section
      className={`border rounded-lg overflow-hidden ${colors.border} ${colors.bg}`}
    >
      <header className={`flex items-center justify-between px-4 py-3 ${colors.header}`}>
        <button
          type="button"
          onClick={onToggleCollapsed}
          disabled={!onToggleCollapsed}
          className="flex items-center gap-2 text-left flex-1 disabled:cursor-default"
        >
          {onToggleCollapsed && (
            collapsed ? (
              <ChevronRight className="w-4 h-4 text-slate-600" />
            ) : (
              <ChevronDown className="w-4 h-4 text-slate-600" />
            )
          )}
          <div>
            <h2 className={`text-base font-semibold ${colors.title}`}>
              {title}{" "}
              <span className={`ml-2 text-sm font-normal ${colors.count}`}>
                {total}
              </span>
            </h2>
            <p className={`text-xs ${colors.description}`}>{description}</p>
          </div>
        </button>

        {!collapsed && total > 0 && (
          <button
            type="button"
            onClick={onSelectAll}
            className="text-xs text-slate-600 hover:text-slate-900 underline"
          >
            Select all
          </button>
        )}
      </header>

      {/* PR 36 (M5 fix). When the bucket is collapsed AND has sources,
          surface the top-N by message volume as a compact preview ribbon.
          The doc's complaint about the Review bucket was "a flat 926 is
          a graveyard; a volume-sorted 926 is a work queue" — the
          volume sort happened client-side in groupSources, but the
          collapsed bucket header still hid every source. The preview
          tells the user where the mass is without making them expand
          the whole list, and each row deep-links to the source so
          opening the heaviest senders is a single click. */}
      {collapsed && sources.length > 0 && (
        <CollapsedBucketPreview
          sources={sources}
          forwarderSources={forwarderSources}
          totalShown={total}
          onExpand={onToggleCollapsed}
        />
      )}

      {!collapsed && (
        <div className="bg-white divide-y divide-slate-100">
          {matcherReviewItems.length > 0 && (
            <div className="px-4 py-2 bg-amber-50">
              <div className="text-xs font-semibold uppercase tracking-wider text-amber-800 mb-2">
                Matcher needs human review ({matcherReviewItems.length})
              </div>
              <ul className="space-y-2">
                {matcherReviewItems.map((item) => (
                  <li
                    key={item.id}
                    className="text-sm bg-white border border-amber-200 rounded p-3"
                  >
                    <div className="font-medium text-slate-900">{item.title}</div>
                    <div className="text-slate-700 mt-1">{item.description}</div>
                    {item.sourceIdentifier && (
                      <div className="mt-1 text-xs text-slate-500">
                        {item.sourceIdentifier}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {sources.length === 0 && forwarderSources.length === 0 && matcherReviewItems.length === 0 && (
            <div className="px-4 py-6 text-sm text-slate-500 text-center">
              Nothing here.
            </div>
          )}

          {sources.length > 0 && (
            <SourceList
              sources={sources}
              mode={mode}
              kind={kind}
              permissions={permissions}
              selectedIds={selectedIds}
              onToggleSelect={onToggleSelect}
              onClassified={onClassified}
              onDismissed={onDismissed}
              onOwnerChanged={onOwnerChanged}
              duplicateOf={duplicateOf}
            />
          )}

          {forwarderSources.length > 0 && (
            <div>
              <div className="px-4 py-2 bg-slate-50 text-xs font-semibold uppercase tracking-wider text-slate-600">
                Forwarders ({forwarderSources.length})
              </div>
              <SourceList
                sources={forwarderSources}
                mode={mode}
                kind={kind}
                permissions={permissions}
                selectedIds={selectedIds}
                onToggleSelect={onToggleSelect}
                onClassified={onClassified}
                onDismissed={onDismissed}
                onOwnerChanged={onOwnerChanged}
                duplicateOf={duplicateOf}
              />
            </div>
          )}
        </div>
      )}
    </section>
  );
}

/**
 * PR 36 (M5 fix). Collapsed-bucket preview ribbon — surfaces the top
 * 3 sources by message volume so an operator scanning a collapsed
 * bucket can see "where the mass is" without expanding. The two
 * scenarios this disambiguates on a Review bucket of 926 sources:
 *
 *   - Top entries are recognisable providers (SendGrid, Mailchimp …)
 *     with auth identities under the customer's domain → genuine
 *     long-tail outreach; user can leave the bucket collapsed.
 *
 *   - Top entries are synthetic-fallback /24 CIDRs → matcher might
 *     need attention; user expands and looks.
 *
 * Each preview row deep-links to the source detail so opening the
 * heaviest sender is one click. The "+N more" row at the bottom
 * expands the bucket itself.
 */
function CollapsedBucketPreview({
  sources,
  forwarderSources,
  totalShown,
  onExpand,
}: {
  sources: SpineSourceListRow[];
  forwarderSources: SpineSourceListRow[];
  totalShown: number;
  onExpand?: () => void;
}) {
  // Pre-sorted by volume in groupSources(); just take the top N
  // across both subgroups. Forwarders are separate visually in the
  // expanded view but go into the same volume ranking here — the
  // user wants to see the heaviest senders regardless of label.
  const pool = [...sources, ...forwarderSources].sort(
    (a, b) => b.messagesInWindow - a.messagesInWindow
  );
  const previewN = 3;
  const top = pool.slice(0, previewN);
  const remaining = totalShown - top.length;

  if (top.length === 0) return null;

  return (
    <div className="px-4 py-2 bg-white/60 border-t border-slate-200 space-y-1">
      {top.map((s) => {
        // PR 53 — insert well-known-ASN label before the raw CIDR /
        // ASN strings so synthetic-fallback sources show as "Google"
        // / "Microsoft" instead of "209.85.220.0/24".
        //
        // PR 73 — match the expanded SourceRow's chain so the
        // collapsed preview doesn't bottom out at `composite_<hash>`
        // for composite rows. `compositeLabel` is server-derived
        // (PR 72) and is always set on composites.
        const isCompositeRow = (s.compositeOf?.length ?? 0) > 0;
        const label =
          s.compositeLabel ??
          s.knownProviderName ??
          (isCompositeRow ? s.vendorBrandedDomain : null) ??
          s.authIdentity ??
          wellKnownAsnLabel(s.asn) ??
          s.cidrBucket ??
          s.asn ??
          s.id;
        // Match the SourceRow's secondary auth-identity rendering
        // (PR 31) so the collapsed preview reads the same as the
        // expanded list when the user does expand.
        const secondary =
          s.knownProviderName !== null && s.authIdentity !== null
            ? s.authIdentity
            : null;
        return (
          <Link
            key={s.id}
            // PR 68 — composite collapsed preview rows route to the
            // composite detail page, mirroring the expanded list.
            to={
              s.compositeOf && s.compositeOf.length > 0
                ? `/sources/composite/${s.id}`
                : `/sources/${s.id}`
            }
            className="flex items-baseline gap-2 text-xs text-slate-700 hover:text-sky-700 hover:bg-slate-50 px-2 py-0.5 -mx-2 rounded"
          >
            <span className="font-medium truncate">{label}</span>
            {secondary && (
              <span className="text-slate-500 font-mono truncate">
                {secondary}
              </span>
            )}
            <span className="ml-auto tabular-nums text-slate-600 flex-shrink-0">
              {s.messagesInWindow.toLocaleString()} msgs
            </span>
          </Link>
        );
      })}
      {remaining > 0 && onExpand && (
        <button
          type="button"
          onClick={onExpand}
          className="block w-full text-left text-xs text-slate-500 hover:text-slate-900 px-2 py-0.5 -mx-2 rounded hover:bg-slate-50"
        >
          + {remaining.toLocaleString()} more
        </button>
      )}
    </div>
  );
}

function bucketColors(kind: BucketKind) {
  switch (kind) {
    case "needs_work":
      return {
        border: "border-rose-200",
        bg: "bg-rose-50",
        header: "bg-rose-100",
        title: "text-rose-900",
        count: "text-rose-700",
        description: "text-rose-800",
      };
    case "review":
      return {
        border: "border-amber-200",
        bg: "bg-amber-50",
        header: "bg-amber-100",
        title: "text-amber-900",
        count: "text-amber-700",
        description: "text-amber-800",
      };
    case "ready":
      return {
        border: "border-emerald-200",
        bg: "bg-emerald-50",
        header: "bg-emerald-100",
        title: "text-emerald-900",
        count: "text-emerald-700",
        description: "text-emerald-800",
      };
    // PR 60 — Threats. Orange/red palette to read as hostile-but-
    // contained. Distinct from `needs_work` (rose, "this is blocking
    // YOU") because Threats traffic isn't blocking anything for the
    // operator; it's "this is what's being attempted *against* you,
    // already neutralised by classification."
    case "threats":
      return {
        border: "border-orange-200",
        bg: "bg-orange-50",
        header: "bg-orange-100",
        title: "text-orange-900",
        count: "text-orange-700",
        description: "text-orange-800",
      };
    case "hidden":
      return {
        border: "border-slate-200",
        bg: "bg-slate-50",
        header: "bg-slate-100",
        title: "text-slate-900",
        count: "text-slate-600",
        description: "text-slate-700",
      };
  }
}

// ---------------------------------------------------------------------------
// SourceList + SourceRow
// ---------------------------------------------------------------------------

interface SourceListProps {
  sources: SpineSourceListRow[];
  mode: string | null;
  kind: BucketKind;
  permissions: SpineSourceListResponse["permissions"];
  selectedIds: Set<string>;
  onToggleSelect: (id: string) => void;
  onClassified: () => void;
  onDismissed: () => void;
  onOwnerChanged: () => void;
  /** PR 64 (#2) — `Map<asUnknownSourceId, canonicalSource>` for the
   *  inline "likely duplicate" hint. Optional; absent → no hints. */
  duplicateOf?: Map<string, SpineSourceListRow>;
}

function SourceList(props: SourceListProps) {
  const { sources, mode, kind, permissions, selectedIds, onToggleSelect, onClassified, onDismissed, onOwnerChanged, duplicateOf } = props;
  return (
    <ul className="divide-y divide-slate-100">
      {sources.map((s) => {
        // PR 67 — composite rows fan out to their underlying member
        // ids for selection. `compositeOf` is the array of real
        // source ids; classify/dismiss/owner endpoints only know
        // those, not the derived composite id. `selected` reads as
        // checked when ANY member is in the selection — toggling
        // adds or removes all members together.
        const memberIds = s.compositeOf ?? [s.id];
        const isComposite = (s.compositeOf?.length ?? 0) > 0;
        const checked = isComposite
          ? memberIds.some((id) => selectedIds.has(id))
          : selectedIds.has(s.id);
        const toggle = () => {
          if (isComposite) {
            // If ANY currently selected, this is a "clear all" toggle.
            // Otherwise add all members.
            for (const id of memberIds) {
              if (checked) {
                if (selectedIds.has(id)) onToggleSelect(id);
              } else {
                if (!selectedIds.has(id)) onToggleSelect(id);
              }
            }
          } else {
            onToggleSelect(s.id);
          }
        };
        return (
          <SourceRow
            key={s.id}
            source={s}
            mode={mode}
            kind={kind}
            permissions={permissions}
            selected={checked}
            onToggleSelect={toggle}
            onClassified={onClassified}
            onDismissed={onDismissed}
            onOwnerChanged={onOwnerChanged}
            duplicateOfCanonical={duplicateOf?.get(s.id) ?? null}
          />
        );
      })}
    </ul>
  );
}

interface SourceRowProps {
  source: SpineSourceListRow;
  mode: string | null;
  kind: BucketKind;
  permissions: SpineSourceListResponse["permissions"];
  selected: boolean;
  onToggleSelect: () => void;
  onClassified: () => void;
  onDismissed: () => void;
  onOwnerChanged: () => void;
  /** PR 64 (#2) — when set, render an inline "likely duplicate of X"
   *  hint linking to the canonical (real-ASN) sibling source. */
  duplicateOfCanonical?: SpineSourceListRow | null;
}

function SourceRow({ source, mode, kind, permissions, selected, onToggleSelect, onClassified, onDismissed, onOwnerChanged, duplicateOfCanonical }: SourceRowProps) {
  // PR 53 — well-known-ASN label sits between authIdentity and
  // cidrBucket. Synthetic-fallback sources with a recognised ASN
  // now display as "Google" instead of "209.85.220.0/24".
  //
  // PR 70 — composites used to bottom out at `source.id` =
  // `composite_<hash>` when the anchor had no knownProviderName or
  // vendorBrandedDomain.
  //
  // PR 72 — server now sets `compositeLabel` on every composite row
  // using the full fallback chain (with a "Composite (N IP groups)"
  // final fallback). The list short-circuits to that when present so
  // composites + the detail page agree on what each group is called.
  const asnLabel = wellKnownAsnLabel(source.asn);
  const isComposite = (source.compositeOf?.length ?? 0) > 0;
  const providerLabel =
    source.compositeLabel ??
    source.knownProviderName ??
    (isComposite ? source.vendorBrandedDomain : null) ??
    source.authIdentity ??
    asnLabel ??
    source.cidrBucket ??
    source.asn ??
    source.id;

  // PR 31 (H2 fix). Two SendGrid sources with different auth identities
  // — one signing d=pacificdebt.com, the other d=sendgrid.net — used
  // to render as identical "SendGrid · pacificdebt.com" rows on the
  // /sources list, indistinguishable until the user opened each one.
  // The auth identity is the third component of the durable spine key,
  // so it's exactly the attribute that splits the two rows; rendering
  // it inline next to the provider name makes the split visible at
  // the grain where it shows.
  //
  // Only render the secondary identifier when:
  //   1. providerLabel was resolved from knownProviderName OR from
  //      the well-known ASN map (so the secondary isn't already the
  //      primary label), AND
  //   2. either an auth identity exists, OR (for synthetic sources
  //      shown by ASN label) the cidr_bucket exists so the operator
  //      can still see exactly which /24 / /64 range is signing.
  const authIdentitySecondary = (() => {
    if (source.knownProviderName !== null && source.authIdentity !== null) {
      return source.authIdentity;
    }
    // PR 53 — when the well-known ASN label is the primary, surface
    // the cidr_bucket inline so operators can still trace the row to
    // a specific /24 / /64 range.
    if (
      source.knownProviderName === null &&
      source.authIdentity === null &&
      asnLabel !== null &&
      source.cidrBucket !== null
    ) {
      return source.cidrBucket;
    }
    return null;
  })();

  // Qualifier tag for the auth identity. Drives off the PR 29 matchRule
  // signal: known-provider-aligned means the customer's own domain is
  // signing (authenticated setup); spf-only-fallback means the ESP's
  // shared signing identity is being used.
  const authIdentityQualifier: { label: string; tone: "good" | "warn" } | null =
    authIdentitySecondary === null
      ? null
      : source.matchRule === "known-provider-aligned"
        ? { label: "authenticated", tone: "good" }
        : source.matchRule === "spf-only-fallback"
          ? { label: "shared", tone: "warn" }
          : null;

  const showAtRiskMetric =
    kind === "needs_work" && source.messagesFailingInWindow > 0;

  return (
    <li className="px-4 py-3 hover:bg-slate-50 transition-colors">
      <div className="flex items-start gap-3">
        <input
          type="checkbox"
          checked={selected}
          onChange={onToggleSelect}
          className="mt-1.5 rounded border-slate-300 text-sky-600 focus:ring-sky-500"
          aria-label={`Select source ${providerLabel}`}
        />

        <div className="flex-1 min-w-0 space-y-1.5">
          <div className="flex flex-wrap items-baseline gap-x-2">
            <Link
              // PR 68 — composite rows route to a dedicated detail
              // page at /sources/composite/:id that shows aggregate
              // stats + the member list. Non-composite rows use the
              // existing per-source detail.
              to={
                source.compositeOf && source.compositeOf.length > 0
                  ? `/sources/composite/${source.id}`
                  : `/sources/${source.id}`
              }
              className="font-medium text-slate-900 hover:text-sky-700 truncate"
            >
              {providerLabel}
            </Link>
            {/* PR 31 (H2). Render the auth identity inline so two
                same-provider rows with different signing identities
                are visually distinguishable on the list — see the
                SendGrid case from the redesign review. */}
            {authIdentitySecondary && (
              <span
                className="text-xs text-slate-500 font-mono truncate"
                title={`Auth identity: ${authIdentitySecondary}`}
              >
                {authIdentitySecondary}
              </span>
            )}
            {authIdentityQualifier && (
              <span
                className={`inline-flex items-center px-1.5 py-0 rounded-full text-[10px] font-medium ${
                  authIdentityQualifier.tone === "good"
                    ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                    : "bg-amber-50 text-amber-700 border border-amber-200"
                }`}
                title={
                  authIdentityQualifier.tone === "good"
                    ? `This source signs with your own domain — DMARC aligns.`
                    : `This source signs with the provider's shared domain — DMARC does not align.`
                }
              >
                {authIdentityQualifier.label}
              </span>
            )}
            <span className="text-xs text-slate-500">{source.domainName}</span>
            {/* PR 67 — composite row affix. When the API's compose
                pass merged 2+ siblings into this row, surface the
                count so the operator knows it's an aggregate. The
                composite row navigates to the dominant member's
                detail page (PR 68 will add a dedicated composite
                detail page; this chip is the breadcrumb in the
                meantime). */}
            {source.compositeCount && source.compositeCount > 1 && (
              <span
                className="inline-flex items-center px-1.5 py-0 rounded-full text-[10px] font-medium bg-slate-50 text-slate-700 border border-slate-200"
                title={`Aggregates ${source.compositeCount} sibling sources sharing the same provider, domain, classification, and bucket. The matcher's per-/24 grain produced separate rows for each IP block; the list collapses them so you see one row per logical sender.`}
              >
                {source.compositeCount} IP groups
              </span>
            )}
            {/* PR 54 — third-party vendor identified by reverse-DNS.
                E.g. a SendGrid stream whose IPs reverse to debtpaypro.com
                is operated by DebtPayPro, not by SendGrid directly. The
                chip is the actionable lead: this is who you contact when
                the stream needs work.

                PR 62 — when we have a known provider and the auth
                identity aligns with the customer's own domain (PSL-
                relaxed), surface that as an explicit "direct send"
                label. Without it, two SendGrid rows on the same page
                — one that's the customer's own send and one routed
                through a third-party subuser — look identical at a
                glance. The signal: no vendor PTR branding + auth_identity
                shares the customer's org-domain → customer's own
                infrastructure through the provider. */}
            {source.vendorBrandedDomain &&
            providerLabel !== source.vendorBrandedDomain ? (
              // PR 70 — suppress the chip when the primary label already
              // IS the vendor (composite case). Otherwise the row reads
              // "mimecast.com · operated by mimecast.com" which is noise.
              <span
                className="inline-flex items-center px-1.5 py-0 rounded-full text-[10px] font-medium bg-indigo-50 text-indigo-700 border border-indigo-200"
                title={`Operated by ${source.vendorBrandedDomain} — identified via forward-confirmed reverse DNS on this stream's sending IPs.`}
              >
                operated by {source.vendorBrandedDomain}
              </span>
            ) : (
              source.knownProviderId &&
              source.authIdentity &&
              source.domainName &&
              getOrgDomain(source.authIdentity) ===
                getOrgDomain(source.domainName) && (
                <span
                  className="inline-flex items-center px-1.5 py-0 rounded-full text-[10px] font-medium bg-slate-50 text-slate-600 border border-slate-200"
                  title={`Direct send — mail leaves this provider signed as ${source.authIdentity} (your domain), no third-party operator identified by reverse-DNS.`}
                >
                  direct send
                </span>
              )
            )}
            {source.confidencePinned && (
              <span
                className="inline-flex items-center gap-0.5 text-xs text-amber-700"
                title="Matcher pinned this as needing human review"
              >
                <Pin className="w-3 h-3" />
                Pinned
              </span>
            )}
            {/* PR 64 (#2) — likely-duplicate hint. The matcher's
                synthetic key splits the same logical source when one
                observation's ASN is AS-UNKNOWN and another's is the
                real ASN. The canonical (real-ASN) sibling has more
                signal; surface it inline so the operator can merge
                via the existing PR 3 split/merge tools. */}
            {duplicateOfCanonical && (
              <Link
                to={`/sources/${duplicateOfCanonical.id}`}
                className="inline-flex items-center gap-1 text-xs px-1.5 py-0 rounded-full font-medium bg-slate-50 text-slate-600 border border-slate-200 hover:bg-slate-100"
                onClick={(e) => e.stopPropagation()}
                title={`Likely the same source as ${duplicateOfCanonical.knownProviderName ?? duplicateOfCanonical.authIdentity ?? duplicateOfCanonical.cidrBucket ?? duplicateOfCanonical.id} — same /24 on this domain, but the matcher split on AS-UNKNOWN vs ${duplicateOfCanonical.asn}. Open the canonical row to merge.`}
              >
                ↑ likely duplicate
              </Link>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2 text-xs">
            <ClassificationBadge classification={source.classification} />
            {source.health && <HealthBadge health={source.health} />}
            {source.knownProviderCategory && (
              <span className="text-slate-500">
                {source.knownProviderCategory}
              </span>
            )}
            {source._tierRestricted && (
              <span className="inline-flex items-center gap-0.5 text-slate-400">
                <Lock className="w-3 h-3" />
                Upgrade for details
              </span>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-600">
            <span>
              <span className="font-medium text-slate-900">
                {source.messagesInWindow.toLocaleString()}
              </span>{" "}
              msgs
            </span>
            {showAtRiskMetric && (
              <span className="text-rose-700">
                <span className="font-medium">
                  {source.messagesFailingInWindow.toLocaleString()}
                </span>{" "}
                {atRiskLabel(mode)}
              </span>
            )}
            {source.healthDmarcPassRate !== null && (
              <span>
                {Math.round((source.healthDmarcPassRate ?? 0) * 100)}% DMARC pass
              </span>
            )}
            {source.lastSeenAt && (
              <span>last seen {formatRelative(source.lastSeenAt)}</span>
            )}
          </div>

          {/* Differential-diagnosis recommendation (Phase 1 PR 17).
              Suppressed for `kind=none` defensive defaults and for
              the resolved/info cases on Ready-bucket rows to avoid
              "all clear" noise on a screen full of healthy senders. */}
          {source.recommendation.kind !== "none" &&
            !(kind === "ready" && source.recommendation.severity === "info") && (
              <RecommendationText
                recommendation={source.recommendation}
                variant="compact"
              />
            )}
        </div>

        <div className="flex flex-col items-end gap-2 flex-shrink-0">
          {permissions.canLabelSources && (
            // Phase 3 PR 28 — wrap classify in the SPF budget guard
            // so an Approve click that would push the chain past the
            // RFC 7208 limit shows a confirm modal first. The guard
            // pass-throughs when the budget fetch fails or the
            // Source has no known_provider with an spfInclude.
            <SpfBudgetClassifyGuard
              domainId={source.domainId}
              knownProviderId={source.knownProviderId}
              childProps={{
                sourceId: source.id,
                currentClassification: source.classification,
                // PR 34. Plumb provider category through so the
                // state-aware action selector can suppress
                // "It's a forwarder" on ESP/CRM/marketing/transactional
                // sources (their category proves they're not forwarders).
                providerCategory: source.knownProviderCategory,
                dismissed: source.dismissedAt !== null,
                onClassified,
                onDismissed,
              }}
            />
          )}
          {/* Owner avatar + reassign menu (PR 20). Compact variant so
              the row's right column doesn't grow vertically — the
              avatar carries the at-a-glance signal; the dropdown is
              one click away when the user wants to reassign. */}
          <SourceOwnerSelect
            sourceId={source.id}
            owner={source.owner}
            variant="compact"
            onChange={onOwnerChanged}
          />
          <Link
            // PR 68 — composite rows route to the dedicated composite
            // detail page; non-composite rows use per-source detail.
            to={
              source.compositeOf && source.compositeOf.length > 0
                ? `/sources/composite/${source.id}`
                : `/sources/${source.id}`
            }
            className="text-xs text-sky-700 hover:text-sky-900 inline-flex items-center gap-0.5"
          >
            Open <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
      </div>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Badges
// ---------------------------------------------------------------------------

function ClassificationBadge({ classification }: { classification: SpineClassification }) {
  switch (classification) {
    case "Approved":
      return <Badge color="emerald" icon={ShieldCheck}>Approved</Badge>;
    case "Forwarder":
      return <Badge color="sky" icon={Repeat}>Forwarder</Badge>;
    case "Not-Mine":
      return <Badge color="rose" icon={ShieldAlert}>Not mine</Badge>;
    case "Unclassified":
    default:
      return <Badge color="slate" icon={HelpCircle}>Unclassified</Badge>;
  }
}

function HealthBadge({ health }: { health: Exclude<SpineHealth, null> }) {
  switch (health) {
    case "Aligned":
      return <Badge color="emerald" icon={CheckCircle2}>Aligned</Badge>;
    case "Misconfigured":
      return <Badge color="rose" icon={AlertTriangle}>Misconfigured</Badge>;
    case "Insufficient-Data":
      return <Badge color="slate" icon={HelpCircle}>Not enough data</Badge>;
  }
}

interface BadgeProps {
  color: "emerald" | "sky" | "rose" | "slate" | "amber";
  icon: React.ComponentType<{ className?: string }>;
  children: React.ReactNode;
}

function Badge({ color, icon: Icon, children }: BadgeProps) {
  const palette: Record<BadgeProps["color"], string> = {
    emerald: "bg-emerald-100 text-emerald-800 border-emerald-200",
    sky: "bg-sky-100 text-sky-800 border-sky-200",
    rose: "bg-rose-100 text-rose-800 border-rose-200",
    slate: "bg-slate-100 text-slate-700 border-slate-200",
    amber: "bg-amber-100 text-amber-800 border-amber-200",
  };
  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-xs font-medium ${palette[color]}`}
    >
      <Icon className="w-3 h-3" />
      {children}
    </span>
  );
}

// ---------------------------------------------------------------------------
// BulkActionBar
// ---------------------------------------------------------------------------

interface BulkActionBarProps {
  count: number;
  sourceIds: string[];
  onClear: () => void;
  onClassified: () => void;
}

function BulkActionBar({ count, sourceIds, onClear, onClassified }: BulkActionBarProps) {
  return (
    <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50">
      <div className="bg-slate-900 text-white rounded-lg shadow-2xl px-4 py-3 flex flex-wrap items-center gap-3">
        <span className="font-medium text-sm">
          {count} source{count === 1 ? "" : "s"} selected
        </span>
        <div className="h-6 w-px bg-slate-700" />
        <SourceClassifyControl
          mode="bulk"
          sourceIds={sourceIds}
          onClassified={onClassified}
        />
        <button
          type="button"
          onClick={onClear}
          className="text-sm text-slate-300 hover:text-white"
        >
          Clear
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

interface GroupedBuckets {
  needsWork: SpineSourceListRow[];
  review: SpineSourceListRow[];
  ready: {
    approved: SpineSourceListRow[];
    forwarders: SpineSourceListRow[];
  };
  /** PR 60 — Not-Mine sources (auto-classified spoofs + user-flagged). */
  threats: SpineSourceListRow[];
  hidden: SpineSourceListRow[];
}

/**
 * Group sources by server-assigned bucket. Within Needs Work, sort
 * descending by the at-risk metric (messagesFailingInWindow) — this is
 * the same number regardless of domain mode; only the label framing
 * differs. Within Review and Ready, sort by messagesInWindow desc so
 * the largest mail flows surface first.
 *
 * `showHidden` re-includes sources the server assigned bucket=null
 * (dismissed + Not-Mine).
 */
function groupSources(
  sources: SpineSourceListRow[],
  showHidden: boolean
): GroupedBuckets {
  const out: GroupedBuckets = {
    needsWork: [],
    review: [],
    ready: { approved: [], forwarders: [] },
    threats: [],
    hidden: [],
  };

  for (const s of sources) {
    switch (s.bucket) {
      case "needs_work":
        out.needsWork.push(s);
        break;
      case "review":
        out.review.push(s);
        break;
      case "ready":
        if (s.classification === "Forwarder") {
          out.ready.forwarders.push(s);
        } else {
          out.ready.approved.push(s);
        }
        break;
      case "threats":
        // PR 60 — Not-Mine bucket; always visible by default so the
        // operator sees what's attempting to impersonate the domain.
        // Dismissed sources still go to `hidden` via the `null` case.
        out.threats.push(s);
        break;
      case null:
        if (showHidden) out.hidden.push(s);
        break;
    }
  }

  out.needsWork.sort(
    (a, b) => b.messagesFailingInWindow - a.messagesFailingInWindow
  );
  out.review.sort((a, b) => b.messagesInWindow - a.messagesInWindow);
  out.ready.approved.sort((a, b) => b.messagesInWindow - a.messagesInWindow);
  out.ready.forwarders.sort((a, b) => b.messagesInWindow - a.messagesInWindow);
  out.threats.sort((a, b) => b.messagesInWindow - a.messagesInWindow);
  out.hidden.sort((a, b) => b.messagesInWindow - a.messagesInWindow);

  return out;
}

function filterBuckets(buckets: GroupedBuckets, q: string): GroupedBuckets {
  const trimmed = q.trim().toLowerCase();
  if (trimmed.length === 0) return buckets;
  const match = (s: SpineSourceListRow): boolean => {
    return (
      (s.knownProviderName ?? "").toLowerCase().includes(trimmed) ||
      // PR 70 — search the PTR-identified vendor (e.g. "mimecast")
      // so the user can find vendor-only rows / composites whose
      // primary label is the vendor and not a knownProviderName.
      (s.vendorBrandedDomain ?? "").toLowerCase().includes(trimmed) ||
      (s.authIdentity ?? "").toLowerCase().includes(trimmed) ||
      (s.domainName ?? "").toLowerCase().includes(trimmed) ||
      (s.asn ?? "").toLowerCase().includes(trimmed) ||
      // PR 53 — search the well-known display name (e.g. "Google")
      // so the user can type "google" and find the AS15169 rows.
      (wellKnownAsnLabel(s.asn) ?? "").toLowerCase().includes(trimmed) ||
      (s.cidrBucket ?? "").toLowerCase().includes(trimmed) ||
      (s.knownProviderCategory ?? "").toLowerCase().includes(trimmed)
    );
  };
  return {
    needsWork: buckets.needsWork.filter(match),
    review: buckets.review.filter(match),
    ready: {
      approved: buckets.ready.approved.filter(match),
      forwarders: buckets.ready.forwarders.filter(match),
    },
    threats: buckets.threats.filter(match),
    hidden: buckets.hidden.filter(match),
  };
}

/**
 * PR 64 (#2) — find sources that look like duplicates of another
 * source on the same `(domainId, cidrBucket)` due to an `AS-UNKNOWN`
 * vs real-ASN split.
 *
 * When an observation arrives before the IP enrichment cron has
 * resolved its ASN, the matcher writes `asn = "AS-UNKNOWN"`. Later
 * observations on the same IP get the real ASN. The synthetic key
 * `(domain, asn, /24)` then splits the same logical source into
 * two rows. This helper detects that pattern client-side and
 * returns a `Map<asUnknownSourceId, realAsnSource>` so the list
 * can surface an inline "likely duplicate of X" hint on the
 * AS-UNKNOWN row.
 *
 * Match criteria: same `domainId`, same `cidrBucket`, one source's
 * `asn === "AS-UNKNOWN"`, the other has a non-UNKNOWN ASN. No
 * provider-name or domain-string matching — purely schema-derived.
 */
function detectAsnUnknownDuplicates(
  sources: SpineSourceListRow[]
): Map<string, SpineSourceListRow> {
  const dups = new Map<string, SpineSourceListRow>();
  // Group by `(domainId, cidrBucket)`. Within each group we look for
  // an AS-UNKNOWN row that has a real-ASN sibling.
  const byKey = new Map<string, SpineSourceListRow[]>();
  for (const s of sources) {
    if (!s.cidrBucket || !s.domainId) continue;
    const k = `${s.domainId}::${s.cidrBucket}`;
    const list = byKey.get(k);
    if (list) list.push(s);
    else byKey.set(k, [s]);
  }
  for (const [, group] of byKey) {
    if (group.length < 2) continue;
    const unknownRows = group.filter((s) => s.asn === "AS-UNKNOWN");
    const knownRows = group.filter(
      (s) => s.asn !== null && s.asn !== "AS-UNKNOWN"
    );
    if (unknownRows.length === 0 || knownRows.length === 0) continue;
    // Canonical source: highest message volume among the real-ASN
    // siblings. Most observation traffic wants the survivor.
    const canonical = knownRows.reduce((a, b) =>
      b.messagesInWindow > a.messagesInWindow ? b : a
    );
    for (const u of unknownRows) {
      dups.set(u.id, canonical);
    }
  }
  return dups;
}

/**
 * Map a unified action-items row down to the Review-bucket's
 * SpineMatcherReviewItem shape. The component originally consumed
 * `matcherReviewItems` from /api/sources; PR 14 routes that through
 * the unified /api/action-items endpoint instead. Shape adapter here
 * so the renderer doesn't have to learn a second row type.
 *
 * `metadata.sourceIdentifier` comes from the action-item writer
 * (audit job in Phase 0 PR 7); fall back to sourceId for older rows.
 */
function actionItemToMatcherReview(
  item: import("@/lib/api").ActionItemRow
): SpineMatcherReviewItem {
  const meta = (item.metadata ?? {}) as { sourceIdentifier?: string };
  return {
    id: item.id,
    domainId: item.domainId ?? "",
    sourceId: item.sourceId,
    fingerprint: item.id,
    title: item.title,
    description: item.description,
    severity: item.severity,
    createdAt: item.createdAt,
    sourceIdentifier: meta.sourceIdentifier ?? item.sourceId ?? null,
  };
}

function filterMatcherItems(items: SpineMatcherReviewItem[], q: string): SpineMatcherReviewItem[] {
  const trimmed = q.trim().toLowerCase();
  if (trimmed.length === 0) return items;
  return items.filter(
    (i) =>
      i.title.toLowerCase().includes(trimmed) ||
      i.description.toLowerCase().includes(trimmed) ||
      (i.sourceIdentifier ?? "").toLowerCase().includes(trimmed)
  );
}

/**
 * Mode-aware framing for the at-risk metric in Needs Work. Both modes
 * sort by the same number (dmarc_fail_count over window); only the
 * label changes. Falls back to generic wording for multi-domain views.
 */
function atRiskLabel(mode: string | null): string {
  if (mode === "operate") return "rejected today";
  if (mode === "rollout") return "blocked at next step";
  return "failing DMARC";
}

function formatRelative(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const diffMs = Date.now() - then;
  const minutes = Math.floor(diffMs / 60_000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
}

// ---------------------------------------------------------------------------
// /sources page wrapper. Standalone surface: renders the spine view
// with no lock, exposing the domain selector for cross-domain triage.
// Per the Phase 1 PR 16 IA collapse this URL has no sidebar entry; the
// canonical path is Domains → pick domain → Sources tab. The standalone
// URL remains for deep links + cross-domain admin use.
// ---------------------------------------------------------------------------

export function Sources() {
  return <SpineSourcesView />;
}
